import { tr } from '../i18n/i18n.ts';
import type { NotificationPlan } from '@autoday/core';
import { api } from '../api.ts';
import { isStandalonePwa } from '../platform.ts';
import type { ChannelStatus, NotificationTransport, ScheduleReport } from './types.ts';

export interface ServerNotificationStatus {
  workerHeartbeatAt: string | null;
  workerStale: boolean;
  lastAcceptedAt: string | null;
  nextPlanned: Array<{ fireAt: string; title: string; channel: string }>;
  recentErrors: Array<{ at: string; message: string }>;
  pushConfigured: boolean;
  /** Fält nedan saknas från äldre servrar. */
  deviceSubscribed?: boolean;
  serverHorizonHours?: number;
  lastPendingFireAt?: string | null;
  /** Service workern på enheten rapporterade att en testnotis visades/öppnades. */
  lastDisplayedTest?: { at: string; status: 'displayed' | 'opened' } | null;
  otherDevices?: Array<{ platform: 'web' | 'android' | 'ios'; name: string | null; lastSeenAt: string | null; route: 'web-push' | 'local' | 'none'; localCoverageUntil: string | null; unroutable: number; remotePush?: boolean }>;
  /** Denna native-enhet har en registrerad fjärrpush-token (FCM/APNs). */
  deviceNativePush?: boolean;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(b, (c) => c.charCodeAt(0));
}

/**
 * Dator/PWA: serverplanerade Web Push-notiser. Servern (worker) planerar från synkad data och skickar
 * via webbläsarens push-tjänst; service workern visar notisen även när fliken är stängd – så länge
 * webbläsaren körs/väcks, nätverket fungerar och OS/webbläsarinställningar tillåter det.
 */
export class WebPushTransport implements NotificationTransport {
  readonly channel = 'web-push' as const;
  readonly capacity = 10_000;

  constructor(private deviceId: string) {}

  private async registration(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) return null;
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  }

  async status(): Promise<ChannelStatus> {
    const limitations = [
      tr('Kräver att AutoDay-servern och dess worker är igång, att datorn har nätverk och att webbläsaren får ta emot push.', 'Requires the AutoDay server and its worker to be running, the computer to have a network connection and the browser to be allowed to receive push.'),
      tr('En sovande eller avstängd dator kan inte visa en notis på exakt klockslag; den kan komma när datorn vaknar eller inte alls.', 'A sleeping or switched-off computer cannot show a notification at the exact time; it may arrive when the computer wakes up, or not at all.'),
    ];
    const actions: ChannelStatus['actions'] = [];
    if (!('Notification' in window) || !('PushManager' in window)) {
      return { channel: this.channel, label: tr('Webbläsare – Web Push', 'Browser – Web Push'), permission: 'unsupported', exactAlarms: 'n/a', backgroundCapable: false, limitations: [tr('Webbläsaren stöder inte Web Push.', 'This browser does not support Web Push.'), ...limitations], actions };
    }
    const perm = Notification.permission === 'default' ? 'prompt' : Notification.permission;
    const reg = await this.registration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (perm !== 'granted') actions.push({ id: perm === 'denied' ? 'open-app-settings' : 'request-permission', label: perm === 'denied' ? tr('Tillåt notiser i webbläsarens webbplatsinställningar', 'Allow notifications in the browser’s site settings') : tr('Tillåt notiser', 'Allow notifications') });
    else if (!sub) actions.push({ id: 'subscribe-push', label: tr('Aktivera push på denna enhet', 'Turn on push on this device') });
    if (/iPhone|iPad/.test(navigator.userAgent) && !isStandalonePwa()) {
      limitations.push(tr('På iPhone/iPad fungerar Web Push bara när AutoDay lagts till på hemskärmen.', 'On iPhone/iPad, Web Push only works when AutoDay has been added to the home screen.'));
      actions.push({ id: 'install-pwa', label: tr('Lägg till på hemskärmen', 'Add to home screen') });
    }
    return {
      channel: this.channel,
      label: tr('Dator/webb – serverplanerad Web Push', 'Computer/web – server-planned Web Push'),
      permission: perm,
      exactAlarms: 'n/a',
      backgroundCapable: perm === 'granted' && !!sub,
      limitations,
      actions,
    };
  }

  async requestPermission(): Promise<ChannelStatus> {
    const p = await Notification.requestPermission();
    if (p === 'granted') await this.subscribe();
    return this.status();
  }

  async subscribe(): Promise<void> {
    const reg = (await this.registration()) ?? (await navigator.serviceWorker.register('/sw.js'));
    await navigator.serviceWorker.ready;
    const { publicKey } = await api<{ publicKey: string }>('/api/push/vapid-public-key');
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource });
    await api(`/api/devices/${encodeURIComponent(this.deviceId)}/push-subscription`, { method: 'PUT', body: { subscription: sub.toJSON(), userAgent: navigator.userAgent.slice(0, 200) } });
  }

  async apply(plan: NotificationPlan): Promise<ScheduleReport> {
    // Servern planerar själv från synkad data. Här verifierar vi bara att prenumerationen finns.
    const st = await this.status();
    return {
      at: new Date().toISOString(),
      channel: this.channel,
      requested: plan.scheduled.length,
      accepted: [],
      verifiedPending: null,
      inexact: false,
      errors: st.backgroundCapable ? [] : [tr('Push är inte aktiverat på denna enhet.', 'Push is not turned on on this device.')],
      coverageUntil: null,
      overflowPoints: 0,
    };
  }

  async serverStatus(): Promise<ServerNotificationStatus> {
    return api<ServerNotificationStatus>(`/api/notifications/status?deviceId=${encodeURIComponent(this.deviceId)}`);
  }

  async scheduleTest(fireAt: Date, testId: string): Promise<{ ok: boolean; detail: string }> {
    const r = await api<{ ok: boolean; detail: string }>('/api/notifications/test', { body: { deviceId: this.deviceId, fireAt: fireAt.toISOString(), testId }, idempotencyKey: testId });
    return r;
  }
}

/** Lokal webbprofil utan konto: inga bakgrundsnotiser – det sägs uttryckligen. */
export class NoBackgroundTransport implements NotificationTransport {
  readonly channel = 'none' as const;
  readonly capacity = 500;
  async status(): Promise<ChannelStatus> {
    return {
      channel: 'none',
      label: tr('Lokal webbprofil – begränsat bakgrundsstöd', 'Local web profile – limited background support'),
      permission: typeof Notification !== 'undefined' ? (Notification.permission === 'default' ? 'prompt' : Notification.permission) : 'unsupported',
      exactAlarms: 'n/a',
      backgroundCapable: false,
      limitations: [
        tr('Utan AutoDay-konto och server kan webbläsaren inte påminna dig när AutoDay är stängd.', 'Without an AutoDay account and server, the browser cannot remind you when AutoDay is closed.'),
        tr('Medan AutoDay är öppen visas påminnelser i appen (och som skrivbordsnotis om du tillåter det) – det ersätter inte bakgrundsnotiser.', 'While AutoDay is open, reminders are shown in the app (and as a desktop notification if you allow it) – this does not replace background notifications.'),
        tr('För påminnelser när appen är stängd: använd Android-appen, eller logga in med ett AutoDay-konto och aktivera sedan Web Push här i Notisstatus.', 'For reminders when the app is closed: use the Android app, or sign in with an AutoDay account and then turn on Web Push here in Reminder check.'),
      ],
      actions: [{ id: 'connect-account', label: tr('Logga in eller skapa konto', 'Sign in or create an account') }],
    };
  }
  async requestPermission() {
    if (typeof Notification !== 'undefined') await Notification.requestPermission();
    return this.status();
  }
  async apply(plan: NotificationPlan): Promise<ScheduleReport> {
    return { at: new Date().toISOString(), channel: 'none', requested: plan.scheduled.length, accepted: [], verifiedPending: null, inexact: false, errors: [], coverageUntil: null, overflowPoints: plan.overflowPoints };
  }
  async scheduleTest(): Promise<{ ok: boolean; detail: string }> {
    return { ok: false, detail: tr('Ingen bakgrundskanal finns i en lokal webbprofil utan konto. Testet kan inte bevisa något om stängd app.', 'A local web profile without an account has no background channel. The test cannot prove anything about a closed app.') };
  }
}
